import React, { Fragment, useEffect, useRef, useState, useMemo } from 'react';
import '@/v2/pages/Plans/PlanPage.css'
import { useAtomValue } from 'jotai';
import { userInfoAtom } from '@/data/atoms/utilsAtoms';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useClicks from '@/v2/pages/Plans/hooks/useClicks';
import Toast from '@/utils/toastManager';
import localStorageKeys from '@/constants/localStorageKeys';
import Layout from '@/Layout';
import TopBar from '@/v2/pages/Plans/components/top-bar';
import PlanSwitcher from '@/v2/pages/Plans/components/plan-switcher';
import PricingCard from '@/v2/pages/Plans/components/pricing-card';
import { MONTHLY_PLANS, YEARLY_PLANS } from '@/data/utils/plansData';
import PricingCarousel from '@/v2/pages/Plans/components/plans-carousel';
import { useVerifySubscription } from '@/hook/useVerifySubscription';
import CustomCheckoutModal from '@/component/PlanPage/CustomCheckoutModal';
import { trackPlansPageEvent } from '@/data/utils/plansPageAnalytics';
import axiosConfig from '@/data/utils/axiosConfigInstance';

const Pricing = () => {
	useVerifySubscription();
	const userData = useAtomValue(userInfoAtom);
	const navigate = useNavigate();
	const [isYearly, setIsYearly] = useState(true);
	const plans = isYearly ? YEARLY_PLANS : MONTHLY_PLANS;
	const isMobile = window.innerWidth < 849;
	const [searchParams] = useSearchParams();
	const { handleStartTrial } = useClicks(userData, null, !userData.subscriptionId, null);
	const buttonRef = useRef(null);
	const [autoSubscriptionTriggered, setAutoSubscriptionTriggered] = useState(false);
	const autoSubscribe = searchParams.get('autoSubscribe') === 'true';
	const autoSubscribeWithPlan = JSON.parse(localStorage.getItem(localStorageKeys.AUTO_SUBSCRIBE_WITH_PLAN));
	const [checkoutModal, setCheckoutModal] = useState({ open: false, plan: null });
	const [currentSubscriptionDetails, setCurrentSubscriptionDetails] = useState(null);
	const [currentSubscriptionLoading, setCurrentSubscriptionLoading] = useState(false);
	const isCancelledUser =
		currentSubscriptionDetails?.status === 'cancelled' ||
		(!userData.subscriptionId && Boolean(userData.user?.isTrialAvailed));

	// Find the full plan object from plansData
	const fullPlanObject = useMemo(() => {
		if (!autoSubscribeWithPlan) return null;
		
		const allPlans = [...MONTHLY_PLANS, ...YEARLY_PLANS];
		for (const plan of allPlans) {
			if (plan.hasSeatSelection && plan.seats) {
				const seat = plan.seats.find(s => s.cbItem === autoSubscribeWithPlan.cbItem || s.seatId === autoSubscribeWithPlan.id);
				if (seat) return seat;
			} else if (plan.cbItem === autoSubscribeWithPlan.cbItem || plan.id === autoSubscribeWithPlan.id) {
				return plan;
			}
		}
		return autoSubscribeWithPlan;
	}, [autoSubscribeWithPlan]);

	useEffect(() => {
		const shouldAutoSubscribe =
			autoSubscribe &&
			fullPlanObject &&
			userData.token &&
			userData.user?.email &&
			!autoSubscriptionTriggered;
		
		if (shouldAutoSubscribe) {
			const timeout = setTimeout(() => {
				try {
					buttonRef.current?.click();
					setAutoSubscriptionTriggered(true);
				} catch (error) {
					Toast.error(error.message || 'Auto-subscription failed.');
				}
			}, 500);

			return () => clearTimeout(timeout);
		}
	}, [autoSubscribe, fullPlanObject, userData, autoSubscriptionTriggered]);

	useEffect(() => {
		if (!userData.subscriptionId) {
			setCurrentSubscriptionDetails(null);
			setCurrentSubscriptionLoading(false);
			return;
		}

		let isActive = true;
		setCurrentSubscriptionLoading(true);

		axiosConfig
			.get(`${import.meta.env.VITE_APP_API_URL}/chargebee/subscription-management-details`)
			.then(response => {
				if (isActive) {
					setCurrentSubscriptionDetails(response.data || null);
				}
			})
			.catch(error => {
				console.error('[Plans] Failed to load current subscription details:', error);
				if (isActive) {
					setCurrentSubscriptionDetails(null);
				}
			})
			.finally(() => {
				if (isActive) {
					setCurrentSubscriptionLoading(false);
				}
			});

		return () => {
			isActive = false;
		};
	}, [userData.subscriptionId]);

	const handleHiddenButtonClick = () => {
		try {
			trackPlansPageEvent({
				eventName: 'auto_subscribe_triggered',
				planId: fullPlanObject?.cbItem || fullPlanObject?.id,
				billingCycle: fullPlanObject?.id?.includes('Monthly') ? 'monthly' : 'yearly',
				source: 'plans_page_auto_subscribe'
			});
			handleStartTrial();
		} catch (error) {
			Toast.error(error.message || 'Auto-subscription click failed.');
		}
	};
	return (
		<>
			<Layout title="Pricing" description="Subscription Plans for The Godfrey Method - On Demand">
				<div className="w-100 mb-5">
					<TopBar />
					<div className="w-100">
						<div className="pt-2 d-flex justify-content-center mb-2">
							<PlanSwitcher onToggle={setIsYearly} />
						</div>
						{isMobile ? (
							<PricingCarousel
								plans={plans}
								isYearly={isYearly}
								onOpenCheckout={setCheckoutModal}
								currentSubscriptionDetails={currentSubscriptionDetails}
								currentSubscriptionLoading={currentSubscriptionLoading}
							/>
						) : (
							<div className="pricing-desktop">
								{plans.map((plan, index) => (
									<Fragment key={plan.id}>
										<PricingCard
											key={plan.id}
											plan={plan}
											isBestValue={index === 1}
											isYearly={isYearly}
											isMobile={false}
											onOpenCheckout={setCheckoutModal}
											currentSubscriptionDetails={currentSubscriptionDetails}
											currentSubscriptionLoading={currentSubscriptionLoading}
										/>
									</Fragment>
								))}
							</div>
						)}
					</div>
				</div>
				<CustomCheckoutModal
					open={checkoutModal.open}
					onClose={() => setCheckoutModal({ open: false, plan: null })}
					plan={checkoutModal.plan}
					isCancelledUser={isCancelledUser}
				/>
				{autoSubscribe && fullPlanObject && (
					<button
						ref={buttonRef}
						className="d-none"
						data-cb-type="checkout"
						data-cb-item-0={fullPlanObject.cbItem}
						onClick={handleHiddenButtonClick}
						id="hidden-subscribe-btn"
					>
						Auto Subscribe
					</button>
				)}
			</Layout>
		</>
	);
};

export default Pricing;
